/**
 * SectionList — Tree-aware drag-and-drop section list for the page editor.
 *
 * Renders a tree structure with:
 * - One DndContext at root (never nested)
 * - Separate SortableContext per container (root + each container)
 * - Disclosure chevrons, indentation, and scoped drag reordering
 */

import { useState, useCallback, useEffect } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Plus, GripVertical, Type, ImageIcon, MousePointerClick, Minus, Space, Sparkles, Search, LayoutGrid, Globe, Phone, Layers } from 'lucide-react'
import { isContainerType, WIDGET_REGISTRY, type SectionLookup, type BlockNode, type BlockGroupConfig } from '@zaviago/storefront-config'
import { SectionListItem } from './SectionListItem'
import { ContainerChildren } from './ContainerChildren'
import type { EditorSection } from './usePageSections'

// ---------------------------------------------------------------------------
// Block layer icon map (matches SectionConfigPanel's BLOCK_ICON)
// ---------------------------------------------------------------------------

const BLOCK_LAYER_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  text: Type,
  'rich-text': Layers,
  image: ImageIcon,
  button: MousePointerClick,
  divider: Minus,
  spacer: Space,
  icon: Sparkles,
  'search-bar': Search,
  'category-nav': LayoutGrid,
  'social-link': Globe,
  'contact-item': Phone,
}

/** Get a short display label for a block in the layer tree */
function getBlockLayerLabel(block: BlockNode): string {
  switch (block.content.type) {
    case 'text':
      return (block.content.value || 'Empty text').slice(0, 30)
    case 'rich-text':
      return `Rich Text (${block.content.items.length})`
    case 'button':
      return (block.content.label || 'Button').slice(0, 30)
    case 'image':
      return block.content.alt || 'Image'
    case 'search-bar':
      return block.content.placeholder || 'Search Bar'
    case 'category-nav':
      return block.content.buttonLabel || 'Category Nav'
    case 'social-link':
      return block.content.platform
    case 'contact-item':
      return block.content.value || block.content.contactType
    default:
      return block.type.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }
}

// ---------------------------------------------------------------------------
// Block sortable ID encoding — prefix to distinguish from section drags
// Format: "block:{sectionEditorId}:{groupId}:{blockId}"
// ---------------------------------------------------------------------------

const BLOCK_PREFIX = 'block:'

function encodeBlockSortId(sectionEditorId: string, groupId: string, blockId: string): string {
  return `${BLOCK_PREFIX}${sectionEditorId}:${groupId}:${blockId}`
}

function decodeBlockSortId(sortId: string): { sectionEditorId: string; groupId: string; blockId: string } | null {
  if (!sortId.startsWith(BLOCK_PREFIX)) return null
  const rest = sortId.slice(BLOCK_PREFIX.length)
  const parts = rest.split(':')
  if (parts.length < 3) return null
  return { sectionEditorId: parts[0], groupId: parts[1], blockId: parts.slice(2).join(':') }
}

/** A single sortable block row in the layer tree */
export function SortableBlockRow({
  block,
  sortId,
  onSelectSection,
}: {
  block: BlockNode
  sortId: string
  onSelectSection: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  }
  const Icon = BLOCK_LAYER_ICON[block.type] ?? Type
  const label = getBlockLayerLabel(block)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group/block flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-gray-50"
      onClick={onSelectSection}
    >
      <button
        className="flex-shrink-0 cursor-grab touch-none rounded p-0.5 text-gray-200 opacity-0 hover:text-gray-400 active:cursor-grabbing group-hover/block:opacity-100"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3 w-3" />
      </button>
      <Icon className="h-3.5 w-3.5 shrink-0 text-gray-300" />
      <span className="truncate text-gray-500">{label}</span>
    </div>
  )
}

/** Look up blockGroups config for a section type from WIDGET_REGISTRY */
function getBlockGroups(sectionType: string): BlockGroupConfig[] | undefined {
  const def = WIDGET_REGISTRY[sectionType] ?? WIDGET_REGISTRY[sectionType.replace('code:', '')]
  return def?.blockGroups
}

/** Renders sortable block rows grouped by groupId with labels */
export function SortableBlockLayerRows({
  blocks,
  sectionEditorId,
  sectionType,
  depth,
  onSelectSection,
}: {
  blocks: BlockNode[]
  sectionEditorId: string
  sectionType: string
  depth: number
  onSelectSection: () => void
}) {
  const groups = getBlockGroups(sectionType)

  // If no blockGroups defined, render flat (original behavior)
  if (!groups || groups.length === 0) {
    const sorted = [...blocks].sort((a, b) => a.layout.order - b.layout.order)
    const sortIds = sorted.map((b) => encodeBlockSortId(sectionEditorId, '_flat', b.id))
    return (
      <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-0.5" style={{ paddingLeft: (depth + 1) * 20 }}>
          {sorted.map((block, i) => (
            <SortableBlockRow
              key={block.id}
              block={block}
              sortId={sortIds[i]}
              onSelectSection={onSelectSection}
            />
          ))}
        </div>
      </SortableContext>
    )
  }

  // Grouped rendering — separate SortableContext per group
  return (
    <div className="flex flex-col gap-1" style={{ paddingLeft: (depth + 1) * 20 }}>
      {groups.map((group) => {
        const groupBlocks = blocks
          .filter((b) => b.layout.groupId === group.id)
          .sort((a, b) => a.layout.order - b.layout.order)
        if (groupBlocks.length === 0) return null
        const sortIds = groupBlocks.map((b) => encodeBlockSortId(sectionEditorId, group.id, b.id))
        return (
          <div key={group.id}>
            <div className="px-1 pb-0.5 pt-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-300">
              {group.label}
            </div>
            <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-0.5">
                {groupBlocks.map((block, i) => (
                  <SortableBlockRow
                    key={block.id}
                    block={block}
                    sortId={sortIds[i]}
                    onSelectSection={onSelectSection}
                  />
                ))}
              </div>
            </SortableContext>
          </div>
        )
      })}
    </div>
  )
}

interface SectionListProps {
  sections: EditorSection[]
  onReorder: (activeId: string, overId: string) => void
  onReorderChildren: (parentEditorId: string, activeId: string, overId: string) => void
  onReorderBlocks: (editorId: string, groupId: string, activeBlockId: string, overBlockId: string) => void
  onDelete: (editorId: string) => void
  selectedId: string | null
  onSelect: (editorId: string) => void
  findByEditorId: (editorId: string) => SectionLookup<EditorSection> | null
  onAddChild: (parentEditorId: string, depth: number) => void
  onMoveToContainer: (editorId: string, containerId: string) => void
  onMoveToRoot: (editorId: string) => void
  onDuplicate: (editorId: string) => void
  onSelectFirstBlock?: (editorId: string) => void
}

export function SectionList({
  sections,
  onReorder,
  onReorderChildren,
  onReorderBlocks,
  onDelete,
  selectedId,
  onSelect,
  findByEditorId,
  onAddChild,
  onMoveToContainer,
  onMoveToRoot,
  onDuplicate,
  onSelectFirstBlock,
}: SectionListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  // Expand/collapse state — default: all containers with children + sections with blocks are expanded
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const set = new Set<string>()
    function walk(items: EditorSection[]) {
      for (const s of items) {
        if (isContainerType(s.type) && s.children?.length) {
          set.add(s._editorId)
          walk(s.children)
        }
        if (s.blocks?.length) {
          set.add(s._editorId)
        }
      }
    }
    walk(sections)
    return set
  })

  const toggleExpand = useCallback((editorId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(editorId)) next.delete(editorId)
      else next.add(editorId)
      return next
    })
  }, [])

  // Auto-expand containers when they gain children (e.g. after addChildSection)
  useEffect(() => {
    setExpandedSections((prev) => {
      let changed = false
      const next = new Set(prev)
      function walk(items: EditorSection[]) {
        for (const s of items) {
          if (isContainerType(s.type) && s.children?.length && !next.has(s._editorId)) {
            next.add(s._editorId)
            changed = true
          }
          if (s.blocks?.length && !next.has(s._editorId)) {
            next.add(s._editorId)
            changed = true
          }
          if (s.children) walk(s.children)
        }
      }
      walk(sections)
      return changed ? next : prev
    })
  }, [sections])

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeId = String(active.id)
    const overId = String(over.id)

    // Block drag — prefixed IDs stay within their section AND group
    const activeBlock = decodeBlockSortId(activeId)
    const overBlock = decodeBlockSortId(overId)
    if (activeBlock && overBlock && activeBlock.sectionEditorId === overBlock.sectionEditorId && activeBlock.groupId === overBlock.groupId) {
      onReorderBlocks(activeBlock.sectionEditorId, activeBlock.groupId, activeBlock.blockId, overBlock.blockId)
      return
    }

    // Section drag
    const lookup = findByEditorId(activeId)
    if (!lookup) return

    if (lookup.parent) {
      // Child drag — reorder within parent container
      onReorderChildren(
        (lookup.parent as EditorSection)._editorId,
        activeId,
        overId,
      )
    } else {
      // Root drag — reorder at root level
      onReorder(activeId, overId)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sections.map((s) => s._editorId)}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col gap-1.5" role="tree">
          {sections.map((section, i) => {
            const isContainer = isContainerType(section.type)
            const blocks = section.blocks ?? []
            const hasBlocks = blocks.length > 0
            const isExpanded = expandedSections.has(section._editorId)
            const children = section.children ?? []

            return (
              <div key={section._editorId}>
                <SectionListItem
                  section={section}
                  indexLabel={String(i + 1)}
                  depth={0}
                  isContainer={isContainer}
                  hasBlocks={hasBlocks}
                  isExpanded={isExpanded}
                  childCount={isContainer ? children.length : blocks.length}
                  isSelected={selectedId === section._editorId}
                  onSelect={onSelect}
                  onDelete={onDelete}
                  onToggleExpand={toggleExpand}
                  sections={sections}
                  onMoveToContainer={onMoveToContainer}
                  onMoveToRoot={onMoveToRoot}
                  onAddChild={onAddChild}
                  onDuplicate={onDuplicate}
                  onSelectFirstBlock={onSelectFirstBlock}
                />

                {/* Block layer rows — sortable via @dnd-kit, grouped by groupId */}
                {hasBlocks && !isContainer && isExpanded && (
                  <SortableBlockLayerRows
                    blocks={blocks}
                    sectionEditorId={section._editorId}
                    sectionType={section.type}
                    depth={0}
                    onSelectSection={() => onSelect(section._editorId)}
                  />
                )}

                {/* Render nested children + inline add button for containers */}
                {isContainer && (isExpanded || children.length === 0) && (
                  <>
                    {children.length > 0 && (
                      <ContainerChildren
                        childSections={children}
                        parentEditorId={section._editorId}
                        parentIndex={i + 1}
                        depth={1}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        onDelete={onDelete}
                        expandedContainers={expandedSections}
                        onToggleExpand={toggleExpand}
                        onAddChild={onAddChild}
                        sections={sections}
                        onMoveToContainer={onMoveToContainer}
                        onMoveToRoot={onMoveToRoot}
                        onDuplicate={onDuplicate}
                        onSelectFirstBlock={onSelectFirstBlock}
                      />
                    )}
                    {children.length === 0 && (
                      <button
                        onClick={() => onAddChild(section._editorId, 1)}
                        className="flex items-center gap-1 border-b border-dashed border-gray-200 px-2 py-1.5 text-xs text-gray-400 transition-colors hover:text-sky-500"
                        style={{ paddingLeft: 20 }}
                      >
                        <Plus className="h-3 w-3" />
                        Add section
                      </button>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </SortableContext>
    </DndContext>
  )
}
